import React, { createContext, useState, useCallback, useEffect } from "react";
import authAxios from "../api/authAxios";
import { useAuth } from "./AuthContext";

export const NotificationContext = createContext();

export function NotificationProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);

  // Fetch notifications from database
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await authAxios.get('/api/support/notifications/');
      if (response.data) {
        const mapped = response.data.map(notif => ({
          id: notif.id,
          type: notif.notification_type || 'info',
          title: notif.title || 'Notification',
          message: notif.message || '',
          timestamp: notif.created_at,
          read: notif.is_read
        }));
        setNotifications(mapped);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  }, []);

  // Monitor auth status to load/clear notifications
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications();
    } else {
      setNotifications([]);
    }
  }, [isAuthenticated, fetchNotifications]);

  const addNotification = useCallback((typeOrMessage, titleOrType, message) => {
    const tempId = Date.now();

    // Play sound (Professional Double Chime)
    try {
      const audio = new Audio("https://assets.mixkit.co/active_storage/sfx/598/598-preview.mp3");
      audio.volume = 0.8;
      audio.play().catch(e => console.log("Audio play failed (user interaction needed first)", e));
    } catch (e) {
      // Ignore audio errors
    }

    let actualType = "info";
    let actualTitle = "Notification";
    let actualMessage = "";

    if (message === undefined) {
      // Handles 2-argument calls: addNotification(message, type)
      actualMessage = typeOrMessage;
      actualType = titleOrType || "info";
      actualTitle = actualType.charAt(0).toUpperCase() + actualType.slice(1);
    } else {
      // Handles 3-argument calls: addNotification(type, title, message)
      actualType = typeOrMessage;
      actualTitle = titleOrType;
      actualMessage = message;
    }

    // Ensure message is always a string
    const messageStr = typeof actualMessage === 'string' ? actualMessage : (typeof actualMessage === 'object' && actualMessage?.detail ? actualMessage.detail : String(actualMessage || ''));
    
    const newNotification = {
      id: tempId,
      type: actualType,
      title: actualTitle,
      message: messageStr,
      timestamp: new Date(),
      read: false,
    };

    // Add locally for zero-latency UX
    setNotifications((prev) => [newNotification, ...prev]);

    // Persist to backend if authenticated
    if (isAuthenticated) {
      authAxios.post('/api/support/notifications/', {
        title: actualTitle,
        message: messageStr,
        notification_type: actualType,
        is_read: false
      })
      .then(response => {
        if (response.data && response.data.id) {
          // Update temp ID with database-persisted values
          setNotifications(prev => prev.map(n => n.id === tempId ? {
            ...n,
            id: response.data.id,
            timestamp: response.data.created_at
          } : n));
        }
      })
      .catch(err => {
        console.error("Failed to persist notification:", err);
      });
    }

    return tempId;
  }, [isAuthenticated]);

  const markAsRead = useCallback((id) => {
    setNotifications((prev) =>
      prev.map((notif) =>
        notif.id === id ? { ...notif, read: true } : notif
      )
    );
    if (isAuthenticated) {
      authAxios.post(`/api/support/notifications/${id}/mark_read/`)
        .catch(err => console.error("Failed to mark notification as read:", err));
    }
  }, [isAuthenticated]);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) =>
      prev.map((notif) => ({ ...notif, read: true }))
    );
    if (isAuthenticated) {
      authAxios.post('/api/support/notifications/mark_all_read/')
        .catch(err => console.error("Failed to mark all notifications as read:", err));
    }
  }, [isAuthenticated]);

  const clearNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((notif) => notif.id !== id));
    if (isAuthenticated) {
      authAxios.delete(`/api/support/notifications/${id}/`)
        .catch(err => console.error("Failed to delete notification:", err));
    }
  }, [isAuthenticated]);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
    if (isAuthenticated) {
      authAxios.post('/api/support/notifications/clear_all/')
        .catch(err => console.error("Failed to clear all notifications:", err));
    }
  }, [isAuthenticated]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        addNotification,
        markAsRead,
        markAllAsRead,
        clearNotification,
        clearAllNotifications,
        unreadCount,
        refreshNotifications: fetchNotifications
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}
